import { Component, Inject, Optional } from '@angular/core';
import { CommunicationUtilisateurDto } from '../model/CommunicationUtilisateurDto';
import { CommunicationUtilisateurService } from '../communication-utilisateur.service';
import { TranslateModule } from '@ngx-translate/core';
import { CommunicationUtilisateurParLangueDto } from '../model/CommunicationUtilisateurParLangueDto';
import { MessageService } from 'primeng/api';
import { AuthCoreService } from '../../../services/auth-core.service';
import { ApplicationErreur } from '../../../exception/ApplicationErreur';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ToastModule } from 'primeng/toast';
import { ButtonModule } from 'primeng/button';
import { isNullOrUndefined } from '../../../utils/functions';
import { AbstractSigaleComponent } from '../../abstract-sigale.component';

@Component({
	standalone: true,
	selector: 'gestion-communication-utilisateur-component',
	templateUrl: './gestion-communication-utilisateur.component.html',
	styleUrls: ['./gestion-communication-utilisateur.component.scss'],
	imports: [
		CommonModule,
		FormsModule,
		TranslateModule,
		SelectButtonModule,
		ToastModule,
		ButtonModule
	]
})
export class GestionCommunicationUtilisateurComponent extends AbstractSigaleComponent {

	messageParLangue: CommunicationUtilisateurParLangueDto[] = [];
	communicationUtilisateur: CommunicationUtilisateurDto;
	langue: string;

	couleursPossibles = [];
	loading = false;
	loadingTraduction = false;
	couleurSelectionnee: string;

	constructor(private communicationUtilisateurService: CommunicationUtilisateurService,
				public messageService: MessageService,
				private authService: AuthCoreService,
				@Optional() @Inject('$rootScope') private $rootScope: any) {
		super();
	}

	ngOnInit(): void {
		super.ngOnInit();
		this.couleursPossibles.push({label: this.translateService.instant('SIGALE_ANGULAR.COMPONENTS.COMMUNICATION_UTILISATEUR.BLEU'), value: 'BLEU'});
		this.couleursPossibles.push({label: this.translateService.instant('SIGALE_ANGULAR.COMPONENTS.COMMUNICATION_UTILISATEUR.ORANGE'), value: 'ORANGE'});

		this.langue = this.authService.determinerLangueDepuisToken();

		this.recupererMessagesInformations();
	}

	/**
	 * Vérifie qu'un message d'information n'a été diffusé aux utilisateurs.
	 */
	recupererMessagesInformations() {
		this.loading = true;
		this.communicationUtilisateurService.recupererCommunicationUtilisateur().subscribe(data => {
			this.loading = false;
			this.communicationUtilisateur = data;
			this.messageParLangue = data.messageParLangue;
			this.couleurSelectionnee = this.communicationUtilisateur.couleur;
		}, error => {
			this.loading = false;
			this.communicationUtilisateur = undefined;
			this.afficherToastError(error.error);
		});
	}

	/**
	 * Vérifie si on peut supprimer ou diffuser le message.
	 * @returns {boolean} True s'il y a un message et une couleur sélectionnée.
	 */
	peutSupprimerOuDiffuser(): boolean {
		return this.communicationUtilisateur && !!this.couleurSelectionnee && this.messageParLangue.every(messageLangue => messageLangue.message && messageLangue.message !== '');
	}

	/**
	 * Vérifie si on peut traduire le message.
	 * @returns {boolean} True s'il y a un message renseigné dans la langue de l'utilisateur.
	 */
	peutTraduireMessage(): boolean {
		const messageSource = this.messageParLangue.find(message => message.langue.code === this.determinerLangueAUtiliser());
		return !isNullOrUndefined(messageSource) && !isNullOrUndefined(messageSource.message) && messageSource.message !== '';
	}

	/**
	 * Détermine le code de la langue à utiliser.
	 * @returns {string} le code de la langue à utiliser.
	 */
	determinerLangueAUtiliser(): string {
		return this.$rootScope ? this.$rootScope.retournerUtilisateurConnecte().langue : this.langue;
	}

	/**
	 * Traduit le message français en italien.
	 */
	traduireMessage(): void {
		this.loadingTraduction = true;
		const codeLangueSource = this.determinerLangueAUtiliser();
		const messageSource = this.messageParLangue.find(message => message.langue.code === codeLangueSource);

		const codesLanguesCibles = this.messageParLangue.map(message => message.langue.code).filter(code => code !== codeLangueSource);

		this.communicationUtilisateurService.traduireVersLanguesDestinations(codeLangueSource, codesLanguesCibles, messageSource.message).subscribe(data => {
			this.loadingTraduction = false;
			this.messageParLangue.forEach(function (messageLangue) {
				if (!isNullOrUndefined(data[messageLangue.langue.code])) {
					messageLangue.message = data[messageLangue.langue.code];
				}
			});
		}, error => {
			this.loadingTraduction = false;
			this.afficherToastError(error.error);
		});
	}

	/**
	 * Enregistre le message.
	 * @param suppression Si true, on envoie un message vide au back, sinon on envoie le message saisi ainsi que la couleur sélectionnée.
	 */
	enregistrerEtDiffuser(suppression): void {
		this.loading = true;
		this.communicationUtilisateur.couleur = this.couleurSelectionnee;
		this.communicationUtilisateur.messageParLangue = this.messageParLangue;

		if (suppression) {
			this.communicationUtilisateur.messageParLangue = [];
			this.messageParLangue.forEach(messageLangue => messageLangue.message = undefined);
			this.communicationUtilisateur.couleur = undefined;
		}
		this.communicationUtilisateurService.diffuserMessages(this.communicationUtilisateur).subscribe(() => {
			this.loading = false;
			if (suppression) {
				this.afficherToastSuccess(this.translateService.instant('SIGALE_ANGULAR.COMPONENTS.COMMUNICATION_UTILISATEUR.MESSAGE_SUPPRIME'));
			}
			else {
				this.afficherToastSuccess(this.translateService.instant('SIGALE_ANGULAR.COMPONENTS.COMMUNICATION_UTILISATEUR.MESSAGE_ENREGISTRE_DIFFUSE'));
			}
		}, error => {
			this.loading = false;
			this.afficherToastError(error.error);
		});
	}

	public afficherToastSuccess(message: string): void {
		this.messageService.add({severity: 'success', detail: message});
	}

	public afficherToastError(message: ApplicationErreur): void {
		this.messageService.add({severity: 'error', detail: message.messageAvecCode});
	}

	traductionFR(): any {
		return {
			"SIGALE_ANGULAR": {
				"COMPONENTS": {
					"COMMUNICATION_UTILISATEUR": {
						"TITRE_SECTION": "Communiquer le message suivant aux utilisateurs",
						"LABEL_MESSAGE": "Message d'information ({{langue}})",
						"TITLE_BTN_DIFFUSER": "Enregistrer et communiquer le message aux utilisateurs",
						"BTN_DIFFUSER": "Communiquer",
						"TITLE_BTN_SUPPRIMER": "Supprime le message",
						"BTN_SUPPRIMER": "Supprimer",
						"BTN_TRADUIRE": "Traduire le message",
						"LABEL_COULEUR": "Couleur du message",
						"BLEU": "Bleu",
						"ORANGE": "Orange",
						"MESSAGE_ENREGISTRE_DIFFUSE": "Le message a été enregistré et diffusé",
						"MESSAGE_SUPPRIME": "Le message a été supprimé"
					}
				}
			}
		};
	}


	traductionIT(): any {
		return {
			"SIGALE_ANGULAR": {
				"COMPONENTS": {
					"COMMUNICATION_UTILISATEUR": {
						"TITRE_SECTION": "Comunicare il messaggio seguente ai utenti",
						"LABEL_MESSAGE": "Messaggio d'informazione ({{langue}})",
						"TITLE_BTN_DIFFUSER": "Salvare e comunicare il messaggio ai utenti",
						"BTN_DIFFUSER": "Comunicare",
						"TITLE_BTN_SUPPRIMER": "Cancellare il messaggio",
						"BTN_SUPPRIMER": "Cancellare",
						"BTN_TRADUIRE": "Traduci il messaggio",
						"LABEL_COULEUR": "Colore del messaggio",
						"BLEU": "Blu",
						"ORANGE": "Arancione",
						"MESSAGE_ENREGISTRE_DIFFUSE": "Il messaggio é stato registrato e diffuso",
						"MESSAGE_SUPPRIME": "Il messaggio é stato cancellato"
					}
				}
			}
		};
	}
}
